import { useMassUpdatePassportGuest } from 'api-hooks/guest';
import formSetErrors from 'common/helpers/form-setError';
import { FilesYupSchema } from 'common/helpers/validation';
import DrawerHeader from 'components/common/drawer/drawer-header';
import { DrawerBody } from 'components/common/drawer/styles';
import notification from 'components/common/notification';
import { PassportType } from 'components/common/parse-excel/parse-passport';
import { Form } from 'components/elements';
import { FormFooter } from 'components/elements/form';
import useYupValidationResolver from 'hooks/use-yup-validation-resolver';
import { FooterDivider } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { Control, useFieldArray, useForm } from 'react-hook-form';
import * as Yup from 'yup';

import PassportItemCard from './components/passport-item-card';

interface Props {
  onClose: () => void;
  result: PassportType[];
}

type PassportItemType = {
  id?: string;
  guestId: string | null;
  type: string | null;
  number: string | null;
  identifierNumber: string | null;
  familyName: string | null;
  givenName: string;
  issueState: string;
  placeOfBirth: string;
  nationality: string;
  gender: string | null;
  dateOfBirth: Date | null;
  expiredAt: Date | null;
  files: { file: string; url: string }[];
};

type FormType = {
  passports: PassportItemType[];
};

function Documents(props: { control: Control<FormType, any> }) {
  const { control } = props;
  const { fields, remove } = useFieldArray({ control, name: 'passports' });

  const onRemove = (idx: number) => remove(idx);

  return (
    <>
      {fields.map((field, idx) => (
        <PassportItemCard key={field.id} idx={idx} onRemove={onRemove} />
      ))}
    </>
  );
}

export default function MassUpdatePassportPanel(props: Props) {
  const { t } = useTranslation();
  const { result } = props;

  const { mutateAsync } = useMassUpdatePassportGuest();

  const defaultValues = React.useMemo<FormType>(
    () => ({
      passports: result.map((item) => ({
        guestId: null,
        type: null,
        files: [],
        number: item.documentNo || '',
        expiredAt: item.expireDate || null,
        dateOfBirth: item.birthday || null,
        givenName: item.givenName || '',
        identifierNumber: item.personalNo || '',
        issueState: item.issueState || '',
        placeOfBirth: item.birthPlace || '',
        familyName: item.familyName || '',
        nationality: item.nationality || '',
        gender: item.sex === 'F' ? 'female' : 'male',
      })),
    }),
    [result],
  );

  const resolver = useYupValidationResolver(
    Yup.object()
      .shape({
        passports: Yup.array()
          .of(
            Yup.object({
              type: Yup.string().nullable().required(),
              number: Yup.string().nullable().required(),
              identifierNumber: Yup.string().nullable().required(),
              familyName: Yup.string().nullable().required(),
              givenName: Yup.string().nullable().required(),
              issueState: Yup.string().nullable().required(),
              nationality: Yup.string().nullable().required(),
              placeOfBirth: Yup.string().nullable().required(),
              gender: Yup.string().nullable().required(),
              dateOfBirth: Yup.date().nullable().required(),
              expiredAt: Yup.date().nullable().required(),
              files: FilesYupSchema.min(1).required(),
            }).required(),
          )
          .required(),
      })
      .required(),
  );

  const methods = useForm<FormType>({
    defaultValues,
    resolver,
    mode: 'onChange',
  });

  const onSubmit = async (values: FormType) => {
    try {
      const mutateResult = await mutateAsync(values);
      notification.success({ message: mutateResult?.message });

      props.onClose();
    } catch (e) {
      if (e.errors) {
        formSetErrors(e.errors, methods.setError);
      }
      notification.error({ message: e.message });
    }
  };

  return (
    <>
      <DrawerHeader
        title={t('guest:guest_documents')}
        onClose={props.onClose}
      />
      <DrawerBody style={{ marginTop: 0 }}>
        <Form methods={methods} onSubmit={onSubmit}>
          <div
            style={{
              flexDirection: 'column',
              display: 'flex',
            }}
          >
            <Documents control={methods.control} />
            <FooterDivider />
          </div>
          <FormFooter showCancel onCancel={props.onClose} />
        </Form>
      </DrawerBody>
    </>
  );
}
